import { PhoneNumberValidationError } from '@mfe/app/services/phoneValidationApiRequests';
import { addError } from '@mfe-helpers/datadog';
import {
    SmsServiceCode,
    ResultStatus,
    ValidationMethods,
} from '@mfe-presentation/constants/userVerification';
import { IStep } from '@mfe-types/form';
import { AxiosError } from 'axios';

const formatError = (error: AxiosError): ResultStatus | SmsServiceCode => {
    if (!error?.response?.data) {
        return ResultStatus.UnexpectedError;
    }

    const { phoneNumber, postalCode, ownerObjective, platform, portal } = error.response
        .data as PhoneNumberValidationError;

    // Sms service was called
    if (phoneNumber && 'status' in phoneNumber) {
        return phoneNumber.status;
    }

    // Sms service wasn't called. The api sent back a validation error first
    if (phoneNumber) {
        return phoneNumber[0].code || ResultStatus.UnexpectedError;
    }

    if (postalCode) {
        return postalCode[0].code || ResultStatus.UnexpectedError;
    }

    if (ownerObjective) {
        return ownerObjective[0].code || ResultStatus.UnexpectedError;
    }

    if (platform) {
        return platform[0].code || ResultStatus.UnexpectedError;
    }

    if (portal) {
        return portal[0].code || ResultStatus.UnexpectedError;
    }

    return ResultStatus.UnexpectedError;
};

const getValidationMethod = (status: ResultStatus) => {
    switch (status) {
        case ResultStatus.SmsSent:
            return ValidationMethods.SmsCode;
        case ResultStatus.Whitelisted:
            return ValidationMethods.Whitelisted;
        case ResultStatus.NoFurtherValidationNeeded:
            return ValidationMethods.RegexOnly;
        case ResultStatus.NotAMobilePhoneNumber:
        case ResultStatus.ServiceUnavailable:
            return ValidationMethods.UnableToVerifyAtCreation;
        default:
            return null;
    }
};

const isVerificationRequired = (status: ResultStatus) => {
    switch (status) {
        // Go to verification step:
        case ResultStatus.SmsSent: // verification code sent
        case ResultStatus.NotAMobilePhoneNumber: // must edit landline number
            return true;
        // Go to result page:
        case ResultStatus.Whitelisted: // verification not mandatory
        case ResultStatus.NoFurtherValidationNeeded: // verification complete
        case ResultStatus.ServiceUnavailable: // verification bypassed
        default: // verification status unknown
            return false;
    }
};

const useSmsService = async (fieldValues: Record<string, any>, config: IStep, name: string) => {
    const {
        ownerObjective,
        platform,
        portal,
        postalCode,
        phoneNumberRegionCode,
        phoneNumber,
        authorContactDetailsId,
        email,
    } = fieldValues;

    try {
        if (!authorContactDetailsId && (!phoneNumberRegionCode || !phoneNumber || !email)) {
            return;
        }

        const validatePhoneFn = config[name].httpCalls?.validatePhone;
        if (!validatePhoneFn) {
            return;
        }

        const {
            verificationRequestId,
            status,
            phoneNumber: phoneNumberStored,
        } = await validatePhoneFn({
            ownerObjective,
            phoneNumber,
            phoneNumberRegionCode,
            platform,
            portal,
            postalCode,
            authorContactDetailsId,
            email,
        });

        return {
            validationMethod: getValidationMethod(status),
            isVerificationRequired: isVerificationRequired(status),
            ...(verificationRequestId && { verificationRequestId }),
            phoneNumber: phoneNumberStored,
            status,
        };
    } catch (error: any) {
        const formatedError = formatError(error);

        addError(error, { payload: fieldValues });

        throw new Error(formatedError);
    }
};

export default useSmsService;
